import type {
  ShipmentRouteField,
  ShipmentRouteMilestone,
  ShipmentRouteStop,
} from '@/services/api/shipments';
import { isFileFieldType } from '@/services/api/stopWorkflows';

/**
 * The ops-evidence rules for one milestone, in one place: how many photos each
 * field needs, whether a milestone is empty / partial / complete, and the exact
 * `values` the server's `validateMilestoneValues` accepts. The modal's gate, the
 * "Save evidence only" enablement, the save loop and the per-field badges all
 * read from here, so they can never disagree about what "complete" means.
 */

/** What ops has typed / uploaded for one milestone, keyed by field key. */
export interface MilestoneDraft {
  photos: Record<string, string[]>;
  texts: Record<string, string>;
}

export type FieldStatus = 'empty' | 'partial' | 'complete';
export type MilestoneStatus = 'empty' | 'partial' | 'complete';

/**
 * The milestone's frozen field list. Falls back to `photoFields` (one required
 * file each, typed IMAGE unless the BE says SIGNATURE) when the BE does not
 * send `fields`, so an older API still works.
 */
export const evidenceFields = (
  milestone: ShipmentRouteMilestone
): ShipmentRouteField[] =>
  milestone.fields && milestone.fields.length > 0
    ? milestone.fields
    : milestone.photoFields.map((f) => ({
        fieldKey: f.fieldKey,
        label: f.label,
        type: f.type ?? 'IMAGE',
        required: true,
        config: null,
        value: null,
      }));

/**
 * Photos a file field needs. SIGNATURE is always exactly one (the server
 * rejects 0 or 2+); IMAGE uses its `imageCount`, default 1 (like the BE).
 */
export const requiredPhotoCount = (field: ShipmentRouteField): number => {
  if (field.type === 'SIGNATURE') return 1;
  const n = Number(field.config?.imageCount);
  return Number.isInteger(n) && n > 0 ? n : 1;
};

const isFilledNumber = (raw: string | undefined): boolean =>
  raw !== undefined && raw.trim() !== '' && Number.isFinite(Number(raw));

/**
 * One field's state. A file field (IMAGE / SIGNATURE) counts photos; TEXT /
 * NUMBER is filled or not.
 */
export const fieldStatus = (
  field: ShipmentRouteField,
  draft: MilestoneDraft
): FieldStatus => {
  if (isFileFieldType(field.type)) {
    const have = draft.photos[field.fieldKey]?.length ?? 0;
    if (have === 0) return 'empty';
    return have >= requiredPhotoCount(field) ? 'complete' : 'partial';
  }
  const raw = draft.texts[field.fieldKey];
  if (field.type === 'NUMBER') return isFilledNumber(raw) ? 'complete' : 'empty';
  return raw && raw.trim() ? 'complete' : 'empty';
};

/**
 * A milestone is complete when every required field is complete and no optional
 * photo field is half-filled (the server accepts an optional field blank or
 * exact, never short). Empty means nothing was touched at all.
 */
export const milestoneStatus = (
  fields: ShipmentRouteField[],
  draft: MilestoneDraft
): MilestoneStatus => {
  const states = fields.map((f) => ({ f, s: fieldStatus(f, draft) }));
  if (states.every(({ s }) => s === 'empty')) return 'empty';
  const ok = states.every(({ f, s }) =>
    f.required ? s === 'complete' : s !== 'partial'
  );
  return ok ? 'complete' : 'partial';
};

/**
 * The required SIGNATURE fields that still have no photo. Ops has no signature
 * pad, only a photo/scan of the signed delivery note, and a required signature
 * stays required on this path (design 2F) — so while this is non-empty the stop
 * cannot be completed and the modal says why.
 */
export const missingSignatures = (
  fields: ShipmentRouteField[],
  draft: MilestoneDraft
): ShipmentRouteField[] =>
  fields.filter(
    (f) =>
      f.type === 'SIGNATURE' &&
      f.required &&
      fieldStatus(f, draft) !== 'complete'
  );

/**
 * The `values` body for `PATCH …/ops-evidence`. File fields send their URL
 * array (exactly the required count — one for a SIGNATURE), TEXT a trimmed
 * string, NUMBER a number. Blank optional fields are left out — the server
 * accepts that.
 */
export const buildValues = (
  fields: ShipmentRouteField[],
  draft: MilestoneDraft
): Record<string, unknown> => {
  const values: Record<string, unknown> = {};
  for (const field of fields) {
    if (fieldStatus(field, draft) !== 'complete') continue;
    if (isFileFieldType(field.type)) {
      values[field.fieldKey] = (draft.photos[field.fieldKey] ?? []).slice(
        0,
        requiredPhotoCount(field)
      );
    } else if (field.type === 'NUMBER') {
      values[field.fieldKey] = Number(draft.texts[field.fieldKey]);
    } else {
      values[field.fieldKey] = (draft.texts[field.fieldKey] ?? '').trim();
    }
  }
  return values;
};

/** A still-PENDING milestone that carries photo fields, flattened with its stop. */
export interface PendingPhotoMilestone {
  stopId: string;
  stopSequence: number;
  milestoneKey: string;
  milestoneName: string;
  fields: ShipmentRouteField[];
}

/**
 * True when the Mark-completed gate owes nothing for this stop: the driver
 * FAILED it, Ops CANCELLED it, or it was SKIPPED (its only pickup failed, or
 * the route closed it). Each was closed with a reason, its unreached
 * milestones are SKIPPED, and the server's COMPLETE gate ignores it exactly
 * the same way (`force-terminal-shipment.usecase.ts`).
 */
export const stopEvidenceWaived = (
  stop: Pick<ShipmentRouteStop, 'status'>
): boolean =>
  stop.status === 'FAILED' ||
  stop.status === 'CANCELLED' ||
  stop.status === 'SKIPPED';

/**
 * Every photo milestone ops must still supply before "Mark completed", in stop
 * order. A FAILED, CANCELLED or SKIPPED stop is out (`stopEvidenceWaived`) — asking ops
 * for its photos would block a shipment whose other drops were delivered.
 */
export const pendingPhotoMilestones = (
  stops: ShipmentRouteStop[]
): PendingPhotoMilestone[] =>
  [...stops]
    .sort((a, b) => a.sequence - b.sequence)
    .filter((stop) => !stopEvidenceWaived(stop))
    .flatMap((stop) =>
      stop.milestones
        .filter((m) => m.status !== 'COMPLETED' && m.photoFields.length > 0)
        .map((m) => ({
          stopId: stop.id,
          stopSequence: stop.sequence,
          milestoneKey: m.key,
          milestoneName: m.name,
          fields: evidenceFields(m),
        }))
    );

/**
 * A stop the Mark-completed modal lists as information instead of upload
 * slots: failed by the driver or cancelled by Ops. Shown, never counted in the
 * photo progress.
 */
export interface ResolvedStopInfo {
  stopId: string;
  stopSequence: number;
  kind: 'FAILED' | 'CANCELLED' | 'SKIPPED';
  /** "Failed by driver · <reason>" / "Cancelled by Ops · <reason>" / "Failed" /
   *  "Skipped · pickup failed" / "Skipped". */
  label: string;
}

/** Every evidence-waived stop of the route as an info row, in stop order. */
export const resolvedStopInfo = (
  stops: ShipmentRouteStop[]
): ResolvedStopInfo[] =>
  [...stops]
    .sort((a, b) => a.sequence - b.sequence)
    .filter(stopEvidenceWaived)
    .map((stop): ResolvedStopInfo => {
      if (stop.status === 'SKIPPED') {
        return {
          stopId: stop.id,
          stopSequence: stop.sequence,
          kind: 'SKIPPED',
          label:
            stop.skipReason === 'PICKUP_FAILED'
              ? 'Skipped · pickup failed'
              : 'Skipped',
        };
      }
      const cancelled = stop.status === 'CANCELLED';
      // An ops "Mark failed" leaves a FAILED stop without a driver record.
      const head = cancelled
        ? 'Cancelled by Ops'
        : stop.failure
          ? 'Failed by driver'
          : 'Failed';
      const reason = cancelled
        ? stop.cancellation?.reasonLabel
        : stop.failure?.reasonLabel;
      return {
        stopId: stop.id,
        stopSequence: stop.sequence,
        kind: cancelled ? 'CANCELLED' : 'FAILED',
        label: reason ? `${head} · ${reason}` : head,
      };
    });
